import React, { useContext, useEffect, useMemo, useState } from 'react';
import { AppContext } from '../context/AppContext';
import { Worklog } from '../services/Worklog';
import {DescriptionHistoryService} from "../services/DescriptionHistoryService";

const formatElapsed = (elapsedSeconds: number) => {
  const hours = Math.floor(elapsedSeconds / 3600);
  const minutes = Math.floor((elapsedSeconds % 3600) / 60);
  const seconds = elapsedSeconds % 60;
  return `${hours.toString().padStart(2, '0')}:${minutes
    .toString()
    .padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
};

const FloatingTimer: React.FC = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('FloatingTimer must be used within AppContextProvider');
  }
  const { timer, stopTimer, store, i18n, syncQueue } = context;

  const [inView, setInView] = useState<boolean>(true);
  const [elapsed, setElapsed] = useState<string>('00:00:00');
  const [running, setRunning] = useState<boolean>(timer.isRunning());

  // Observe visibility of the timer's elapsed display (small anchor)
  useEffect(() => {
    const target = document.getElementById('elapsed');
    if (!target || typeof IntersectionObserver === 'undefined') {
      // If the anchor isn't in the DOM, treat as "not in view"
      setInView(false);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        setInView(entry.isIntersecting);
      },
      { threshold: 0 } // any pixel visible counts as in view
    );

    observer.observe(target);
    return () => observer.disconnect();
  }, []);

  // Update elapsed and running state every second
  useEffect(() => {
    const tick = () => {
      const isRunning = timer.isRunning();
      setRunning(isRunning);

      // If the anchor element doesn't exist (e.g., on other views), force "not in view"
      if (!document.getElementById('elapsed')) {
        setInView(false);
      }

      if (isRunning) {
        setElapsed(formatElapsed(timer.elapsedSec()));
      }
    };
    const id = window.setInterval(tick, 1000);
    // initial
    tick();
    return () => window.clearInterval(id);
  }, [timer]);

  const visible = useMemo(() => running && !inView, [running, inView]);

  const handleStop = () => {
    // Stop timer
    const worklog = stopTimer();
    if (worklog) {
      // Add description to history
      if (worklog.description) {
        DescriptionHistoryService.addDescription(worklog.description);
      }
      const w = new Worklog(worklog);
      store.add(w);
      syncQueue.enqueueIfAuto(w);
    }
  };

  return (
    <div className={`floating-timer ${visible ? 'visible' : ''}`} aria-live="polite" role="status">
      <span className="time">{elapsed}</span>
      <button
        className="btn danger btn-icon"
        title={i18n.t('stop')}
        aria-label={i18n.t('stop')}
        onClick={handleStop}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          aria-hidden="true"
          focusable="false"
        >
          <rect x="6" y="6" width="12" height="12" fill="currentColor" rx="2" ry="2" />
        </svg>
      </button>
    </div>
  );
};

export default FloatingTimer;
